import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEdit, FiTrash2, FiCopy, FiArchive, FiSearch, FiFilter, FiMoreVertical, FiEye, FiEyeOff } from 'react-icons/fi';
import { getImageUrl } from '../../utils/imageUtils';
import '../../styles/Admin.css';

const ProductTable = ({ products, onDelete, onDuplicate, onArchive }) => {
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Filters state
    const [filters, setFilters] = useState({
        category: 'all',
        stockStatus: 'all',
        priceRange: 'all'
    });

    useEffect(() => {
        let result = [...products];

        // 1. Search
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(p =>
                p.name.toLowerCase().includes(lowerTerm) ||
                (p.description && p.description.toLowerCase().includes(lowerTerm)) ||
                (p.sku && p.sku.toLowerCase().includes(lowerTerm))
            );
        }

        // 2. Tabs
        if (activeTab === 'active') {
            result = result.filter(p => p.visibility === 'active' || (!p.visibility && p.inStock)); // Fallback for old data
        } else if (activeTab === 'drafts') {
            result = result.filter(p => p.visibility === 'draft');
        } else if (activeTab === 'low_stock') {
            result = result.filter(p => p.stockQuantity < (p.lowStockThreshold || 10) && p.stockQuantity > 0);
        } else if (activeTab === 'out_of_stock') {
            result = result.filter(p => p.stockQuantity === 0 || !p.inStock);
        }

        // 3. Sorting
        if (sortConfig.key) {
            result.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredProducts(result);
    }, [products, searchTerm, activeTab, sortConfig, filters]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const toggleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedProducts(filteredProducts.map(p => p.id || p._id));
        } else {
            setSelectedProducts([]);
        }
    };

    const toggleSelectProduct = (id) => {
        if (selectedProducts.includes(id)) {
            setSelectedProducts(selectedProducts.filter(pId => pId !== id));
        } else {
            setSelectedProducts([...selectedProducts, id]);
        }
    };

    const getStockStatus = (product) => {
        if (!product.inStock || product.stockQuantity === 0) return <span className="status-badge out-of-stock">Out of Stock</span>;
        if (product.stockQuantity < (product.lowStockThreshold || 10)) return <span className="status-badge low-stock">Low Stock ({product.stockQuantity})</span>;
        return <span className="status-badge in-stock">In Stock ({product.stockQuantity || 'N/A'})</span>;
    };

    return (
        <div className="product-table-wrapper">
            {/* Top Bar: Search & Tabs */}
            <div className="table-controls">
                <div className="tabs-list">
                    <button className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All</button>
                    <button className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>Active</button>
                    <button className={`tab-btn ${activeTab === 'drafts' ? 'active' : ''}`} onClick={() => setActiveTab('drafts')}>Drafts</button>
                    <button className={`tab-btn ${activeTab === 'low_stock' ? 'active' : ''}`} onClick={() => setActiveTab('low_stock')}>Low Stock</button>
                    <button className={`tab-btn ${activeTab === 'out_of_stock' ? 'active' : ''}`} onClick={() => setActiveTab('out_of_stock')}>Out of Stock</button>
                </div>

                <div className="search-filter-box">
                    <div className="search-input-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, SKU..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {/* Placeholder for advanced filter trigger */}
                    <button className="filter-trigger-btn"><FiFilter /> Filters</button>
                </div>
            </div>

            {/* Bulk Actions Bar (Visible when items selected) */}
            {selectedProducts.length > 0 && (
                <div className="bulk-actions-bar">
                    <span>{selectedProducts.length} selected</span>
                    <div className="bulk-btns">
                        <button className="bulk-btn">Set to Draft</button>
                        <button className="bulk-btn">Archive</button>
                        <button className="bulk-btn delete">Delete</button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="table-responsive">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>
                                <input
                                    type="checkbox"
                                    onChange={toggleSelectAll}
                                    checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                />
                            </th>
                            <th style={{ width: '80px' }}>Image</th>
                            <th onClick={() => handleSort('name')} className="sortable">Product Name</th>
                            <th>SKU</th>
                            <th>Category</th>
                            <th onClick={() => handleSort('price')} className="sortable">Price</th>
                            <th>Stock</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                                <tr key={product.id || product._id} className={selectedProducts.includes(product.id || product._id) ? 'selected-row' : ''}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedProducts.includes(product.id || product._id)}
                                            onChange={() => toggleSelectProduct(product.id || product._id)}
                                        />
                                    </td>
                                    <td>
                                        <div className="product-thumb-wrapper">
                                            <img
                                                src={getImageUrl((product.images && product.images.length > 0) ? product.images[0] : (product.image || 'https://via.placeholder.com/50'))}
                                                alt={product.name}
                                                className="product-thumb-lg"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <Link to={`/admin/edit/${product.id || product._id}`} className="product-name-link">
                                            {product.name}
                                        </Link>
                                    </td>
                                    <td>{product.sku || '-'}</td>
                                    <td>{product.category}</td>
                                    <td>
                                        <div className="price-cell">
                                            <span className="current-price">₹{product.price}</span>
                                            {product.originalPrice && product.originalPrice > product.price && (
                                                <span className="original-price">₹{product.originalPrice}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>{getStockStatus(product)}</td>
                                    <td>
                                        <span className={`status-pill ${product.visibility || 'active'}`}>
                                            {product.visibility || 'Active'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="row-actions">
                                            <Link to={`/admin/edit/${product.id || product._id}`} className="action-icon-btn edit" title="Edit">
                                                <FiEdit />
                                            </Link>
                                            <button onClick={() => onDuplicate && onDuplicate(product)} className="action-icon-btn duplicate" title="Duplicate">
                                                <FiCopy />
                                            </button>
                                            <button onClick={() => onDelete && onDelete(product.id || product._id)} className="action-icon-btn delete" title="Delete">
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="no-data">No products found matching your criteria.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProductTable;
